import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  ArrowLeft,
  Save,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Monitor,
  RefreshCw,
  Send,
  Unlink,
} from 'lucide-react'
import { useTvTemplateById, useTvTemplateDevices, TvTemplateVod } from '@/hooks/useTvTemplates'
import { tvTemplateService } from '@/services/tv_template_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { logger } from '@magnetar/utils'
import { VodDialog } from './VodDialog'
import ApplyDialog from './ApplyDialog'

export default function TvTemplateDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { template, isLoading, mutate } = useTvTemplateById({
    template_id: id || null,
  })
  const {
    devices,
    isLoading: devicesLoading,
    mutate: mutateDevices,
  } = useTvTemplateDevices({
    template_id: id || null,
  })

  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
  })
  const [isSaving, setIsSaving] = useState(false)

  // VOD state
  const [vodDialogOpen, setVodDialogOpen] = useState(false)
  const [selectedVod, setSelectedVod] = useState<TvTemplateVod | null>(null)
  const [deleteVodDialogOpen, setDeleteVodDialogOpen] = useState(false)
  const [vodToDelete, setVodToDelete] = useState<TvTemplateVod | null>(null)
  const [isDeletingVod, setIsDeletingVod] = useState(false)

  // Apply & Sync state
  const [applyDialogOpen, setApplyDialogOpen] = useState(false)
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  // Unbind state
  const [unbindDialogOpen, setUnbindDialogOpen] = useState(false)
  const [deviceToUnbind, setDeviceToUnbind] = useState<{
    broadcast_device_id: number
    name?: string
  } | null>(null)
  const [isUnbinding, setIsUnbinding] = useState(false)

  useEffect(() => {
    if (template) {
      setFormData({
        template_name: template.template_name,
        description: template.description || '',
      })
    }
  }, [template])

  const handleSaveInfo = async () => {
    if (!token || !id) return

    setIsSaving(true)
    try {
      const result = await tvTemplateService.updateTemplate(parseInt(id), formData, token)
      if (result.success) {
        mutate()
      }
    } catch (error) {
      logger.error('更新失敗', error)
    } finally {
      setIsSaving(false)
    }
  }

  // VOD handlers
  const handleAddVod = () => {
    setSelectedVod(null)
    setVodDialogOpen(true)
  }

  const handleEditVod = (vod: TvTemplateVod) => {
    setSelectedVod(vod)
    setVodDialogOpen(true)
  }

  const handleDeleteVod = (vod: TvTemplateVod) => {
    setVodToDelete(vod)
    setDeleteVodDialogOpen(true)
  }

  const confirmDeleteVod = async () => {
    if (!vodToDelete || !token || !id) return

    setIsDeletingVod(true)
    try {
      const result = await tvTemplateService.deleteTemplateVod(
        parseInt(id),
        vodToDelete.template_vod_id,
        token,
      )
      if (result.success) {
        mutate()
      }
    } catch {
      toast.error('刪除失敗')
    } finally {
      setIsDeletingVod(false)
      setDeleteVodDialogOpen(false)
      setVodToDelete(null)
    }
  }

  // Sync handler
  const handleSync = async () => {
    if (!token || !id) return

    setIsSyncing(true)
    try {
      const result = await tvTemplateService.syncTemplate(parseInt(id), token)
      if (result.success) {
        mutateDevices()
      }
    } catch (error) {
      logger.error('同步失敗', error)
    } finally {
      setIsSyncing(false)
      setSyncDialogOpen(false)
    }
  }

  // Unbind handler
  const handleUnbind = (device: { broadcast_device_id: number; name?: string }) => {
    setDeviceToUnbind(device)
    setUnbindDialogOpen(true)
  }

  const confirmUnbind = async () => {
    if (!deviceToUnbind || !token) return

    setIsUnbinding(true)
    try {
      const result = await tvTemplateService.unbindTemplate(
        deviceToUnbind.broadcast_device_id,
        token,
      )
      if (result.success) {
        mutateDevices()
        mutate()
      }
    } catch {
      toast.error('解除綁定失敗')
    } finally {
      setIsUnbinding(false)
      setUnbindDialogOpen(false)
      setDeviceToUnbind(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">找不到模板</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          返回列表
        </Button>
      </div>
    )
  }

  const sortedVods = [...(template.template_vods || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{template.template_name}</h1>
          <p className="text-muted-foreground">{template.description || '無描述'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setApplyDialogOpen(true)}>
            <Send className="mr-2 h-4 w-4" />
            套用到設備
          </Button>
          <Button variant="outline" onClick={() => setSyncDialogOpen(true)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            同步所有設備
          </Button>
        </div>
      </div>

      {/* Section 1: Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
          <CardDescription>編輯模板的基本資訊</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 max-w-lg">
            <div className="space-y-2">
              <Label htmlFor="template_name">模板名稱</Label>
              <Input
                id="template_name"
                value={formData.template_name}
                onChange={(e) =>
                  setFormData({ ...formData, template_name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
              />
            </div>
            <div>
              <Button onClick={handleSaveInfo} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="mr-2 h-4 w-4" />
                儲存
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: VOD List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>VOD 列表</CardTitle>
              <CardDescription>管理模板中的影片列表</CardDescription>
            </div>
            <Button onClick={handleAddVod} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              新增影片
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sortedVods.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">尚未新增任何影片</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>排序</TableHead>
                  <TableHead>影片名稱</TableHead>
                  <TableHead>來源</TableHead>
                  <TableHead>播放類型</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedVods.map((vod) => (
                  <TableRow key={vod.template_vod_id}>
                    <TableCell>{vod.order ?? '-'}</TableCell>
                    <TableCell className="font-medium">{vod.vod_name}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{vod.source}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {vod.broadcast_type === 'tv'
                          ? '電視影片'
                          : vod.broadcast_type === 'clinic_vod'
                            ? '診間影片'
                            : '衛教影片'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={vod.is_active ? 'default' : 'secondary'}>
                        {vod.is_active ? '啟用' : '停用'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditVod(vod)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteVod(vod)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Applied Devices */}
      <Card>
        <CardHeader>
          <CardTitle>已套用設備</CardTitle>
          <CardDescription>目前使用此模板的廣播設備</CardDescription>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              尚未套用到任何設備
            </div>
          ) : (
            <div className="space-y-2">
              {devices.map((device) => (
                <div
                  key={device.broadcast_device_id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {device.name || `設備 ${device.broadcast_device_id}`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {device.clinic?.clinic_name || '未知診所'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnbind(device)}
                  >
                    <Unlink className="mr-1 h-3 w-3" />
                    解除綁定
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* VOD Dialog */}
      <VodDialog
        open={vodDialogOpen}
        onOpenChange={setVodDialogOpen}
        templateId={parseInt(id!)}
        vod={selectedVod}
        onSuccess={() => mutate()}
      />

      {/* Apply Dialog */}
      <ApplyDialog
        open={applyDialogOpen}
        onOpenChange={setApplyDialogOpen}
        templateId={parseInt(id!)}
        appliedDeviceIds={devices.map((d) => d.broadcast_device_id)}
        onSuccess={() => {
          mutateDevices()
          mutate()
        }}
      />

      {/* Sync Confirmation Dialog */}
      <AlertDialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>同步模板到所有設備</AlertDialogTitle>
            <AlertDialogDescription>
              確定要將目前的模板內容同步到所有已套用的設備嗎？這將覆蓋設備上現有的模板 VOD
              配置。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSyncing}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleSync()
              }}
              disabled={isSyncing}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  同步中...
                </>
              ) : (
                '確認同步'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete VOD Confirmation Dialog */}
      <AlertDialog open={deleteVodDialogOpen} onOpenChange={setDeleteVodDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此影片嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原。這將從模板中永久刪除影片 &quot;{vodToDelete?.vod_name}&quot;。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingVod}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmDeleteVod()
              }}
              disabled={isDeletingVod}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingVod ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                '確認刪除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unbind Confirmation Dialog */}
      <AlertDialog open={unbindDialogOpen} onOpenChange={setUnbindDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要解除綁定嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              解除綁定後，設備 &quot;
              {deviceToUnbind?.name || `設備 ${deviceToUnbind?.broadcast_device_id}`}
              &quot; 將不再使用此模板的 VOD 配置。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnbinding}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmUnbind()
              }}
              disabled={isUnbinding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isUnbinding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  解除中...
                </>
              ) : (
                '確認解除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
